import axios from 'axios';
import { BACKEND_BASE_URI } from '../utils/constants';

// Types
export interface Message {
  id?: number;
  role: 'user' | 'assistant';
  text: string;
  created_at: string;
  sources?: Source[];
}

export interface Source {
  type: 'database' | 'web' | 'insight' | 'company_insight';
  title: string;
  company?: string;
  url?: string;
  similarity?: number;
  report_type?: string;
}

export interface Conversation {
  id: number;
  title: string | null;
  created_at?: string;
  updated_at?: string;
  messages?: Message[];
}

export interface ConversationListResponse {
  conversations: Array<{
    conversation_id: number;
    title: string | null;
  }>;
}

export interface ConversationDetailResponse {
  conversation_id: number;
  title: string | null;
  messages: Array<{
    role: 'user' | 'assistant';
    text: string;
    created_at: string;
  }>;
}

// Helper to get auth headers
const getAuthHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// List all conversations for the current user
export const getConversations = async (token: string): Promise<Conversation[]> => {
  try {
    const url = `${BACKEND_BASE_URI}ccconversation/`;
    const response = await axios.get<ConversationListResponse>(url, {
      headers: getAuthHeaders(token),
    });

    return response.data.conversations.map(conv => ({
      id: conv.conversation_id,
      title: conv.title,
    }));
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

// Get a single conversation with all messages
export const getConversation = async (token: string, id: number): Promise<Conversation> => {
  try {
    const url = `${BACKEND_BASE_URI}ccconversation/${id}/`;
    const response = await axios.get<ConversationDetailResponse>(url, {
      headers: getAuthHeaders(token),
    });

    return {
      id: response.data.conversation_id,
      title: response.data.title,
      messages: response.data.messages.map(msg => ({
        role: msg.role,
        text: msg.text,
        created_at: msg.created_at,
      })),
    };
  } catch (error) {
    console.error('Error fetching conversation:', error);
    throw error;
  }
};

// Create a new conversation
export const createConversation = async (token: string, title?: string): Promise<Conversation> => {
  try {
    const url = `${BACKEND_BASE_URI}ccconversation/`;
    const response = await axios.post(
      url,
      { title: title || null },
      { headers: getAuthHeaders(token) }
    );

    return {
      id: response.data.conversation_id,
      title: title || null,
      messages: [],
    };
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

// Delete a conversation
export const deleteConversation = async (token: string, id: number): Promise<void> => {
  try {
    const url = `${BACKEND_BASE_URI}ccconversation/${id}/`;
    await axios.delete(url, {
      headers: getAuthHeaders(token),
    });
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

// Add a message to a conversation
export const addMessage = async (
  token: string,
  conversationId: number,
  role: 'user' | 'assistant',
  text: string
): Promise<{ message_id: number }> => {
  try {
    const url = `${BACKEND_BASE_URI}ccmessage/${conversationId}/`;
    const response = await axios.post(
      url,
      { role, text },
      { headers: getAuthHeaders(token) }
    );

    return {
      message_id: response.data.message_id,
    };
  } catch (error) {
    console.error('Error adding message:', error);
    throw error;
  }
};

// Helper to group conversations by date
export const groupConversationsByDate = (conversations: Conversation[]): Record<string, Conversation[]> => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  const groups: Record<string, Conversation[]> = {
    'Today': [],
    'Yesterday': [],
    'Last 7 Days': [],
    'Older': [],
  };

  conversations.forEach(conv => {
    if (!conv.created_at) {
      groups['Older'].push(conv);
      return;
    }

    const convDate = new Date(conv.created_at);

    if (convDate >= today) {
      groups['Today'].push(conv);
    } else if (convDate >= yesterday) {
      groups['Yesterday'].push(conv);
    } else if (convDate >= lastWeek) {
      groups['Last 7 Days'].push(conv);
    } else {
      groups['Older'].push(conv);
    }
  });

  // Remove empty groups
  Object.keys(groups).forEach(key => {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  });

  return groups;
};
